import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, ChevronDown, CalendarIcon, Filter } from "lucide-react";
import { format } from "date-fns";
import {
  usageApi,
  type AreaPresetStateWithDetails,
  type UsageDetailsParams,
} from "../../lib/usage";
import { type Site } from "../../lib/sites";
import {
  isUsageEventInScope,
  useUsageAccessScope,
} from "../../hooks/useUsageAccessScope";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatPercentOfTotal } from "./usage-duration";

export interface SiteUsageData {
  siteId: string;
  siteName: string;
  totalSeconds: number;
  groups: Map<string, GroupUsageData>;
}

export interface GroupUsageData {
  groupId: string;
  groupName: string;
  totalSeconds: number;
  areas: Map<string, AreaUsageData>;
}

export interface AreaUsageData {
  areaId: string;
  areaName: string;
  totalSeconds: number;
  presets: Map<string, PresetUsageData>;
}

export interface PresetUsageData {
  presetId: string;
  presetName: string;
  totalSeconds: number;
  events: AreaPresetStateWithDetails[];
}

interface UsageSummaryTableProps {
  sites: Site[];
  startDate?: Date;
  endDate?: Date;
  setStartDate?: (date: Date) => void;
  setEndDate?: (date: Date) => void;
  filters?: Partial<UsageDetailsParams>;
  dateRangeParams?: { startDate: string; endDate: string };
  showHeaderFilters?: boolean;
  embedded?: boolean;
  displayMode?: "time" | "percent";
}

const formatDuration = (seconds: number) => {
  if (!seconds) return "0s";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
  // if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

// Component for rendering an individual event row
interface EventRowProps {
  event: AreaPresetStateWithDetails;
  displayMode: "time" | "percent";
  totalSecondsAll: number;
}

const EventRow = ({ event, displayMode, totalSecondsAll }: EventRowProps) => {
  const startTime = new Date(event.startTime);
  const endTime = event.endTime ? new Date(event.endTime) : null;
  const isActive = event.active || !event.endTime;
  
  // Calculate duration
  let durationSec = 0;
  if (endTime) {
    durationSec = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);
  } else if (isActive) {
    durationSec = Math.floor((new Date().getTime() - startTime.getTime()) / 1000);
  } else if (event.duration) {
    durationSec = event.duration;
  }

  return (
    <TableRow className="bg-muted/5">
      <TableCell className="w-8"></TableCell>
      <TableCell className="pl-16 text-sm text-muted-foreground">
        <div className="flex items-center gap-2 flex-wrap">
          <span>On at: {format(startTime, "PPp")}</span>
          {endTime && <span>→ Off at: {format(endTime, "PPp")}</span>}
        </div>
      </TableCell>
      <TableCell className="text-right">
        {isActive && <Badge variant="default">Active</Badge>}
      </TableCell>
      <TableCell className="text-right text-sm">
        {displayMode === "time"
          ? formatDuration(durationSec)
          : formatPercentOfTotal(durationSec, totalSecondsAll)}
      </TableCell>
    </TableRow>
  );
};

// Component for rendering a preset row (now expandable)
interface PresetRowProps {
  preset: PresetUsageData;
  isExpanded: boolean;
  onToggle: () => void;
  displayMode: "time" | "percent";
  totalSecondsAll: number;
}

const PresetRow = ({
  preset,
  isExpanded,
  onToggle,
  displayMode,
  totalSecondsAll,
}: PresetRowProps) => {
  const hasActive = preset.events.some((event) => event.active || !event.endTime);
  
  return (
    <>
      <TableRow className="bg-muted/10">
        <TableCell className="w-8">
          {preset.events.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={onToggle}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          )}
        </TableCell>
        <TableCell className="pl-12">Preset: {preset.presetName}</TableCell>
        <TableCell className="text-right">
          {hasActive && <Badge variant="default">Active</Badge>}
        </TableCell>
        <TableCell className="text-right">
          {displayMode === "time"
            ? formatDuration(preset.totalSeconds)
            : formatPercentOfTotal(preset.totalSeconds, totalSecondsAll)}
        </TableCell>
      </TableRow>
      {isExpanded &&
        preset.events
          .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
          .map((event) => (
            <EventRow
              key={event.id}
              event={event}
              displayMode={displayMode}
              totalSecondsAll={totalSecondsAll}
            />
          ))}
    </>
  );
};

// Component for rendering an area row with its presets
interface AreaRowProps {
  area: AreaUsageData;
  isExpanded: boolean;
  onToggle: () => void;
  expandedPresets: Set<string>;
  onTogglePreset: (areaId: string, presetId: string) => void;
  displayMode: "time" | "percent";
  totalSecondsAll: number;
}

const AreaRow = ({
  area,
  isExpanded,
  onToggle,
  expandedPresets,
  onTogglePreset,
  displayMode,
  totalSecondsAll,
}: AreaRowProps) => {
  const hasActive = Array.from(area.presets.values()).some((preset) =>
    preset.events.some((event) => event.active || !event.endTime)
  );
  
  return (
    <>
      <TableRow className="bg-muted/30">
        <TableCell className="w-8">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={onToggle}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </TableCell>
        <TableCell className="pl-8">Area: {area.areaName}</TableCell>
        <TableCell className="text-right">
          {hasActive && <Badge variant="default">Active</Badge>}
        </TableCell>
        <TableCell className="text-right">
          {displayMode === "time"
            ? formatDuration(area.totalSeconds)
            : formatPercentOfTotal(area.totalSeconds, totalSecondsAll)}
        </TableCell>
      </TableRow>
      {isExpanded &&
        Array.from(area.presets.values())
          .sort((a, b) => b.totalSeconds - a.totalSeconds)
          .map((presetData) => {
            const presetKey = `${area.areaId}-${presetData.presetId}`;
            const isPresetExpanded = expandedPresets.has(presetKey);
            return (
              <PresetRow
                key={presetData.presetId}
                preset={presetData}
                isExpanded={isPresetExpanded}
                onToggle={() => onTogglePreset(area.areaId, presetData.presetId)}
                displayMode={displayMode}
                totalSecondsAll={totalSecondsAll}
              />
            );
          })}
    </>
  );
};

// Component for rendering a group row with its areas
interface GroupRowProps {
  group: GroupUsageData;
  isExpanded: boolean;
  onToggle: () => void;
  expandedAreas: Set<string>;
  onToggleArea: (siteId: string, groupId: string, areaId: string) => void;
  expandedPresets: Set<string>;
  onTogglePreset: (siteId: string, areaId: string, presetId: string) => void;
  displayMode: "time" | "percent";
  totalSecondsAll: number;
}

// Component for rendering a site row with its groups
interface SiteRowProps {
  site: SiteUsageData;
  isExpanded: boolean;
  onToggle: () => void;
  expandedGroups: Set<string>;
  onToggleGroup: (siteId: string, groupId: string) => void;
  expandedAreas: Set<string>;
  onToggleArea: (siteId: string, groupId: string, areaId: string) => void;
  expandedPresets: Set<string>;
  onTogglePreset: (siteId: string, areaId: string, presetId: string) => void;
  displayMode: "time" | "percent";
  totalSecondsAll: number;
}

const GroupRow = ({
  group,
  isExpanded,
  onToggle,
  expandedAreas,
  onToggleArea,
  expandedPresets,
  onTogglePreset,
  siteId,
  displayMode,
  totalSecondsAll,
}: GroupRowProps & { siteId: string }) => {
  return (
    <>
      <TableRow className="font-medium border-t-2">
        <TableCell className="w-8">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={onToggle}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </TableCell>
        <TableCell className="pl-4">Group: {group.groupName}</TableCell>
        <TableCell className="text-right">
          {Array.from(group.areas.values()).some((area) =>
            Array.from(area.presets.values()).some((preset) =>
              preset.events.some((event) => event.active || !event.endTime)
            )
          ) && <Badge variant="default">Active</Badge>}
        </TableCell>
        <TableCell className="text-right">
          {displayMode === "time"
            ? formatDuration(group.totalSeconds)
            : formatPercentOfTotal(group.totalSeconds, totalSecondsAll)}
        </TableCell>
      </TableRow>
      {isExpanded &&
        Array.from(group.areas.values())
          .sort((a, b) => b.totalSeconds - a.totalSeconds)
          .map((areaData) => {
            const areaKey = `${siteId}-${group.groupId}-${areaData.areaId}`;
            const isAreaExpanded = expandedAreas.has(areaKey);
            return (
              <AreaRow
                key={areaKey}
                area={areaData}
                isExpanded={isAreaExpanded}
                onToggle={() => onToggleArea(siteId, group.groupId, areaData.areaId)}
                expandedPresets={expandedPresets}
                onTogglePreset={(areaId, presetId) => onTogglePreset(siteId, areaId, presetId)}
                displayMode={displayMode}
                totalSecondsAll={totalSecondsAll}
              />
            );
          })}
    </>
  );
};

const SiteRow = ({
  site,
  isExpanded,
  onToggle,
  expandedGroups,
  onToggleGroup,
  expandedAreas,
  onToggleArea,
  expandedPresets,
  onTogglePreset,
  displayMode,
  totalSecondsAll,
}: SiteRowProps) => {
  return (
    <>
      <TableRow className="font-bold border-b-2 border-b-primary">
        <TableCell className="w-8">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={onToggle}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </TableCell>
        <TableCell>Site: {site.siteName}</TableCell>
        <TableCell className="text-right">
          {Array.from(site.groups.values()).some((group) =>
            Array.from(group.areas.values()).some((area) =>
              Array.from(area.presets.values()).some((preset) =>
                preset.events.some((event) => event.active || !event.endTime)
              )
            )
          ) && <Badge variant="default">Active</Badge>}
        </TableCell>
        <TableCell className="text-right">
          {displayMode === "time"
            ? formatDuration(site.totalSeconds)
            : formatPercentOfTotal(site.totalSeconds, totalSecondsAll)}
        </TableCell>
      </TableRow>
      {isExpanded &&
        Array.from(site.groups.values())
          .sort((a, b) => b.totalSeconds - a.totalSeconds)
          .map((groupData) => {
            const groupKey = `${site.siteId}-${groupData.groupId}`;
            const isGroupExpanded = expandedGroups.has(groupKey);
            return (
              <GroupRow
                key={groupKey}
                group={groupData}
                siteId={site.siteId}
                isExpanded={isGroupExpanded}
                onToggle={() => onToggleGroup(site.siteId, groupData.groupId)}
                expandedAreas={expandedAreas}
                onToggleArea={onToggleArea}
                expandedPresets={expandedPresets}
                onTogglePreset={onTogglePreset}
                displayMode={displayMode}
                totalSecondsAll={totalSecondsAll}
              />
            );
          })}
    </>
  );
};

interface UsageSummaryTableHeaderProps {
  startDate: Date;
  endDate: Date;
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
}

function UsageSummaryTableHeader({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
}: UsageSummaryTableHeaderProps) {
  const handleQuickSelect = (period: string) => {
    const now = new Date();
    let start: Date;
    let end: Date;

    switch (period) {
      case "1d":
        start = new Date(now);
        start.setDate(start.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "7d":
        start = new Date(now);
        start.setDate(start.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "month":
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case "year":
        start = new Date(now.getFullYear(), 0, 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(now.getFullYear(), 11, 31);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        return;
    }

    setStartDate(start);
    setEndDate(end);
  };

  return (
    <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <CardTitle>Usage Summary</CardTitle>
      
      {/* Mobile: Filter dropdown button (below sm) */}
      <div className="sm:hidden w-full">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="w-full">
              <Filter className="mr-2 h-4 w-4" />
              Filters
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] max-w-[calc(100vw-2rem)] sm:w-80" align="start" side="bottom">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs">Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal text-sm",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">
                        {startDate ? format(startDate, "PP") : "Start date"}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => date && setStartDate(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal text-sm",
                        !endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">
                        {endDate ? format(endDate, "PP") : "End date"}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={(date) => date && setEndDate(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Quick Select</Label>
                <Select onValueChange={handleQuickSelect}>
                  <SelectTrigger className="w-full text-sm">
                    <SelectValue placeholder="Quick select" />
                  </SelectTrigger>
                  <SelectContent position="popper" align="start">
                    <SelectItem value="1d">Last 1 day</SelectItem>
                    <SelectItem value="7d">Last 7 days</SelectItem>
                    <SelectItem value="month">Current month</SelectItem>
                    <SelectItem value="year">Current year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Desktop: Regular filter inputs (sm and above) */}
      <div className="hidden sm:flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
        <div className="flex flex-row items-center gap-2 flex-1 sm:flex-initial">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full sm:w-[140px] md:w-[160px] justify-start text-left font-normal text-sm",
                  !startDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                <span className="truncate">
                  {startDate ? format(startDate, "PP") : "Start date"}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => date && setStartDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full sm:w-[140px] md:w-[160px] justify-start text-left font-normal text-sm",
                  !endDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                <span className="truncate">
                  {endDate ? format(endDate, "PP") : "End date"}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={(date) => date && setEndDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        <Select onValueChange={handleQuickSelect}>
          <SelectTrigger className="w-full sm:w-[140px] text-sm">
            <SelectValue placeholder="Quick select" />
          </SelectTrigger>
          <SelectContent position="popper" align="end">
            <SelectItem value="1d">Last 1 day</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="month">Current month</SelectItem>
            <SelectItem value="year">Current year</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </CardHeader>
  );
}

export default function UsageSummaryTable({
  sites,
  startDate: startDateProp,
  endDate: endDateProp,
  setStartDate: setStartDateProp,
  setEndDate: setEndDateProp,
  filters: filtersProp,
  dateRangeParams: dateRangeParamsProp,
  showHeaderFilters = true,
  embedded = false,
  displayMode = "time",
}: UsageSummaryTableProps) {
  const accessScope = useUsageAccessScope();
  const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set());
  const [expandedPresets, setExpandedPresets] = useState<Set<string>>(new Set());

  // Default to last 7 days (used when this component isn't given shared date state)
  const getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const getDefaultEndDate = () => {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date;
  };

  const [internalStartDate, setInternalStartDate] = useState<Date>(
    getDefaultStartDate()
  );
  const [internalEndDate, setInternalEndDate] = useState<Date>(
    getDefaultEndDate()
  );

  const startDate = startDateProp ?? internalStartDate;
  const endDate = endDateProp ?? internalEndDate;
  const setStartDate = setStartDateProp ?? setInternalStartDate;
  const setEndDate = setEndDateProp ?? setInternalEndDate;

  const filters = filtersProp ?? {};

  /** Sites the viewer may see, intersected with the sites prop when provided. */
  const scopedSites = useMemo(() => {
    if (!accessScope.ready) return [];
    if (!accessScope.allowedSiteIds) return sites;
    return sites.filter((s) => accessScope.allowedSiteIds!.has(s.id));
  }, [sites, accessScope]);

  const computedDateRangeParams = useMemo(() => {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  }, [startDate, endDate]);

  const dateRangeParams = dateRangeParamsProp ?? computedDateRangeParams;

  // Fetch all usage details using getDetails API
  // This returns events with all related data (site, group, area, preset names) already included
  const { data: usageDetails, isLoading, isSuccess } = useQuery({
    queryKey: [
      "usage-details-summary",
      startDate,
      endDate,
      filters,
      accessScope.role,
      scopedSites.map((s) => s.id),
      accessScope.allowedGroupIds
        ? Array.from(accessScope.allowedGroupIds).sort()
        : null,
    ],
    enabled: !!startDate && !!endDate && accessScope.ready,
    queryFn: async () => {
      // Fetch all pages of data by using a large limit
      // The API supports up to 100 items per page, so we'll fetch in chunks
      const limit = 100;
      let page = 1;
      const allEvents: AreaPresetStateWithDetails[] = [];
      let hasMore = true;

      const requestFilters: Partial<UsageDetailsParams> = { ...filters };
      // When exactly one scoped site, narrow the API query for efficiency.
      if (!requestFilters.siteId && scopedSites.length === 1) {
        requestFilters.siteId = scopedSites[0].id;
      }

      while (hasMore) {
        const result = await usageApi.getDetails({
          ...requestFilters,
          ...dateRangeParams,
          page,
          limit,
          sortBy: "startTime",
          sortOrder: "desc",
        });

        allEvents.push(...result.data);

        // Check if there are more pages
        hasMore = page < result.totalPages;
        page++;
      }

      return allEvents;
    },
  });

  // Group usage data by Site -> Group -> Area -> Preset -> Event
  // The getDetails API already includes all the names we need
  const groupedUsageData = useMemo(() => {
    if (!isSuccess || isLoading || !usageDetails) {
      return new Map<string, SiteUsageData>();
    }

    const scopedSiteIdSet = new Set(scopedSites.map((s) => s.id));

    // Build nested structure: Site -> Group -> Area -> Preset -> Event
    const siteMap = new Map<string, SiteUsageData>();

    usageDetails.forEach((event) => {
      // Skip events without required data (groupId can be null for ungrouped events)
      if (!event.siteId || !event.areaId || !event.presetId) return;

      // Always enforce access scope + sites prop intersection client-side
      if (!isUsageEventInScope(event, accessScope)) return;
      if (scopedSiteIdSet.size > 0 && !scopedSiteIdSet.has(event.siteId)) return;

      // Calculate duration from startTime and endTime, or use duration field if available
      let durationSec = 0;
      if (event.endTime) {
        durationSec = Math.floor(
          (new Date(event.endTime).getTime() - new Date(event.startTime).getTime()) / 1000
        );
      } else if (event.active) {
        // For active states, calculate duration up to now
        durationSec = Math.floor(
          (new Date().getTime() - new Date(event.startTime).getTime()) / 1000
        );
      } else if (event.duration) {
        durationSec = event.duration;
      }

      const siteId = event.siteId;
      const siteName = event.siteName || "Unknown Site";
      const groupId = event.groupId;
      const groupName = event.groupName || "Unknown Group";
      const areaId = event.areaId;
      const areaName = event.areaName || "Unknown Area";
      const presetId = event.presetId;
      const presetName = event.presetName || "Unknown Preset";

      // Initialize site if needed
      if (!siteMap.has(siteId)) {
        siteMap.set(siteId, {
          siteId,
          siteName,
          totalSeconds: 0,
          groups: new Map(),
        });
      }

      const siteData = siteMap.get(siteId)!;

      // Initialize group if needed
      if (!siteData.groups.has(groupId)) {
        siteData.groups.set(groupId, {
          groupId,
          groupName,
          totalSeconds: 0,
          areas: new Map(),
        });
      }

      const groupData = siteData.groups.get(groupId)!;

      // Initialize area if needed
      if (!groupData.areas.has(areaId)) {
        groupData.areas.set(areaId, {
          areaId,
          areaName,
          totalSeconds: 0,
          presets: new Map(),
        });
      }

      const areaData = groupData.areas.get(areaId)!;

      // Add preset (merge events if preset already exists)
      const existingPreset = areaData.presets.get(presetId);
      if (existingPreset) {
        existingPreset.totalSeconds += durationSec;
        existingPreset.events.push(event);
      } else {
        areaData.presets.set(presetId, {
          presetId,
          presetName,
          totalSeconds: durationSec,
          events: [event],
        });
      }

      // Update totals
      areaData.totalSeconds += durationSec;
      groupData.totalSeconds += durationSec;
      siteData.totalSeconds += durationSec;
    });

    return siteMap;
  }, [usageDetails, isSuccess, isLoading, accessScope, scopedSites]);

  // Access-scoped usage (API + client filters already applied while grouping)
  const filteredUsageData = groupedUsageData;
  const totalSecondsAll = useMemo(() => {
    let total = 0;
    filteredUsageData.forEach((site) => {
      total += site.totalSeconds;
    });
    return total;
  }, [filteredUsageData]);

  const toggleSite = (siteId: string) => {
    setExpandedSites((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) {
        next.delete(siteId);
        // Also collapse all groups and areas in this site
        const siteData = filteredUsageData.get(siteId);
        if (siteData) {
          siteData.groups.forEach((group: GroupUsageData) => {
            const groupKey = `${siteId}-${group.groupId}`;
            setExpandedGroups((prevGroups) => {
              const nextGroups = new Set(prevGroups);
              nextGroups.delete(groupKey);
              return nextGroups;
            });
            group.areas.forEach((area: AreaUsageData) => {
              const areaKey = `${siteId}-${group.groupId}-${area.areaId}`;
              setExpandedAreas((prevAreas) => {
                const nextAreas = new Set(prevAreas);
                nextAreas.delete(areaKey);
                return nextAreas;
              });
            });
          });
        }
      } else {
        next.add(siteId);
      }
      return next;
    });
  };

  const toggleGroup = (siteId: string, groupId: string) => {
    const key = `${siteId}-${groupId}`;
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        // Also collapse all areas in this group
        const siteData = filteredUsageData.get(siteId);
        if (siteData) {
          const groupData = siteData.groups.get(groupId);
          if (groupData) {
            groupData.areas.forEach((area: AreaUsageData) => {
              const areaKey = `${siteId}-${groupId}-${area.areaId}`;
              setExpandedAreas((prevAreas) => {
                const nextAreas = new Set(prevAreas);
                nextAreas.delete(areaKey);
                return nextAreas;
              });
            });
          }
        }
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleArea = (siteId: string, groupId: string, areaId: string) => {
    const key = `${siteId}-${groupId}-${areaId}`;
    setExpandedAreas((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        // Also collapse all presets in this area
        const siteData = filteredUsageData.get(siteId);
        if (siteData) {
          const groupData = siteData.groups.get(groupId);
          if (groupData) {
            const areaData = groupData.areas.get(areaId);
            if (areaData) {
              areaData.presets.forEach((preset: PresetUsageData) => {
                const presetKey = `${areaId}-${preset.presetId}`;
                setExpandedPresets((prevPresets) => {
                  const nextPresets = new Set(prevPresets);
                  nextPresets.delete(presetKey);
                  return nextPresets;
                });
              });
            }
          }
        }
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const togglePreset = (_siteId: string, areaId: string, presetId: string) => {
    const key = `${areaId}-${presetId}`;
    setExpandedPresets((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const header = showHeaderFilters ? (
    <UsageSummaryTableHeader
      startDate={startDate}
      endDate={endDate}
      setStartDate={setStartDate}
      setEndDate={setEndDate}
    />
  ) : (
    <CardHeader>
      <CardTitle>Usage Summary</CardTitle>
    </CardHeader>
  );

  const body = isLoading || !accessScope.ready ? (
    <div className="grid gap-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-[400px]" />
      <Skeleton className="h-64" />
    </div>
  ) : filteredUsageData.size > 0 ? (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8"></TableHead>
          <TableHead>Name</TableHead>
          <TableHead className="text-right">Active</TableHead>
          <TableHead className="text-right">
            {displayMode === "time" ? "Total Time" : "% of total"}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from(filteredUsageData.values())
          .sort((a: SiteUsageData, b: SiteUsageData) => b.totalSeconds - a.totalSeconds)
          .map((siteData: SiteUsageData) => {
            const isSiteExpanded = expandedSites.has(siteData.siteId);
            return (
              <SiteRow
                key={siteData.siteId}
                site={siteData}
                isExpanded={isSiteExpanded}
                onToggle={() => toggleSite(siteData.siteId)}
                expandedGroups={expandedGroups}
                onToggleGroup={toggleGroup}
                expandedAreas={expandedAreas}
                onToggleArea={toggleArea}
                expandedPresets={expandedPresets}
                onTogglePreset={togglePreset}
                displayMode={displayMode}
                totalSecondsAll={totalSecondsAll}
              />
            );
          })}
      </TableBody>
    </Table>
  ) : (
    <div className="text-muted-foreground">No usage</div>
  );

  if (embedded) {
    return <div className="overflow-x-auto">{body}</div>;
  }

  return (
    <Card>
      {header}
      <CardContent className="overflow-x-auto">{body}</CardContent>
    </Card>
  );
}
